import React from 'react';

import { isEmpty } from 'ramda';

import { ETripEvent } from 'detailsv2/structure';

import {
  useChangePagePopover,
  useDefaultValues as useTripEventDefaultValues,
  useTripEventDefaultsLoader,
} from '../../../TripEventModalContents/shared';
import { useAccomodationWizardContext } from '../../context';
import { usePropertyPageQuery } from '../../hooks';
import { TFormValues } from '../types';
import { getHandleAccommodationData } from './functions';
import { useAccomodationFormContext } from './useAccomodationFormContext';

export const useDefaultValues = () => {
  const { createdRooms, setCreatedRooms, data } = useAccomodationWizardContext();
  const { methods } = useAccomodationFormContext();

  const { watch, setValue, getValues } = methods;
  const attachedPage = watch('attachedPage');

  const handleResetData = React.useCallback((currentData: TFormValues, newPage: { name?: string | null }) => {
    return {
      data: {
        rooming: [],
        notes: null,
        isAlternate: false,
        isCollapsible: false,
        checkIn: '',
        checkOut: '',
        confirmationNumber: '',
        contactInfo: '',
        website: '',
        location: '',
        roomBasis: null,
        displaySettings: {
          checkIn: `Check in at ${newPage.name}`,
          overnight: `Overnight at ${newPage.name}`,
          checkOut: `Check out from ${newPage.name}`,
        },
      },
    };
  }, []);

  const { handleAttachedPage, keepData, resetData, setProceedStatus } = useTripEventDefaultValues<TFormValues>({
    methods,
    onResetData: handleResetData,
    eventType: ETripEvent.Accommodation,
    customDataHandler: getHandleAccommodationData(setCreatedRooms),
  });

  const { savedDefaultValues } = useTripEventDefaultsLoader({
    eventType: ETripEvent.Accommodation,
    page: attachedPage,
  });

  const { onChangePage, popover } = useChangePagePopover<TFormValues>({
    keepData,
    resetData,
    setProceedStatus,
    handleAttachedPage,
    methods,
    prevAttachedPage: data.default.prevAttachedPage,
  });

  /* Call onChangePage after chose "one-off event" (or click "back" button) on creation new event in builder */
  React.useEffect(() => {
    if (attachedPage?.isNew) {
      onChangePage(attachedPage.name, attachedPage.id);
    }
  }, []);

  const propertyPageQueryRes = usePropertyPageQuery({ pageId: attachedPage?.id });

  // TODO: need to move this logic inside the useTripEventDefaultValues hook
  React.useEffect(() => {
    if (!propertyPageQueryRes.data) return;
    if (!propertyPageQueryRes.isValidPropertyPage) return;
    if (savedDefaultValues) return;
    const createdRoomsValues = createdRooms.map((room) => room.value);
    const rooming = getValues().data.rooming;
    if (isEmpty(createdRoomsValues)) return;
    const handledRooming = rooming.filter((roomingItem) => createdRoomsValues.includes(roomingItem.value));
    setValue('data.rooming', handledRooming);
  }, [propertyPageQueryRes.data, savedDefaultValues]);

  return { onChangePage, popover };
};
